import { useState, useEffect } from 'react';
import { useTenantId } from './use-tenant-id';
import { isZoocasaTenant } from 'tenants';
import { useModal, ADDRESS_MODAL } from 'contexts/modal';

/**
 * Header breakpoint matching SCSS $breakpoint-mobile (480px).
 * 
 * The header component uses @include mobile which is (max-width: 480px).
 * This is different from the v2 unified breakpoint system (768px).
 */
const HEADER_MOBILE_BREAKPOINT = 480;

/**
 * Header height constants matching SCSS variables in general.scss
 * 
 * Desktop/Tablet (> 480px):
 * - $height-top-nav: 80px (Zoocasa)
 * - $height-exp-top-nav: 60px (eXp tenants)
 * 
 * Mobile (<= 480px):
 * - $height-top-nav-mobile: 80px (Zoocasa)
 * - $height-exp-top-nav-mobile: 80px (eXp tenants)
 */
const HEADER_HEIGHT = {
  zoocasa: { desktop: 80, mobile: 80 },
  exp: { desktop: 60, mobile: 80 },
} as const;

/**
 * Hook to check if viewport is at header's mobile breakpoint (<= 480px).
 * Uses the same breakpoint as the header SCSS (@include mobile).
 */
function useIsHeaderMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${HEADER_MOBILE_BREAKPOINT}px)`);
    setIsMobile(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

/**
 * Hook to get the current header height based on tenant and viewport.
 * 
 * Uses the header's actual breakpoint (480px) to match SCSS behavior.
 * Returns the appropriate header height for sticky positioning calculations.
 * 
 * If rendered inside an address modal, returns the modal top bar height (64px)
 * instead of the main site header height.
 * 
 * @returns Header height in pixels
 * 
 * @example
 * ```tsx
 * function MyStickyComponent() {
 *   const headerHeight = useHeaderHeight();
 *   return <div style={{ top: headerHeight }}>Sticky content</div>;
 * }
 * ```
 */
export function useHeaderHeight(): number {
  const tenantId = useTenantId();
  const isMobile = useIsHeaderMobile();
  const { activeModal } = useModal();

  // Modal top bar height is 4em = 64px
  if (activeModal === ADDRESS_MODAL) {
    return 64;
  }
  
  const heights = isZoocasaTenant(tenantId) ? HEADER_HEIGHT.zoocasa : HEADER_HEIGHT.exp;
  return isMobile ? heights.mobile : heights.desktop;
}
